import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import * as LucideIcons from "lucide-react";
import {
  Tag, ArrowRight,
  User, Store, HeartHandshake, Trophy, Grid3x3,
} from "lucide-react";
import SectionHeader from "./SectionHeader";

// Suite du bloc services : bande d'inscription multi-profils + barre d'univers.
// (Les actions devis/bons plans sont désormais dans le bloc « Nos services ».)

// Tuiles d'inscription — la tuile Pro est surlignée (ruban Offre Fondateur).
const SIGNUPS = [
  {
    to: "/inscription", testId: "signup-particulier", icon: User,
    label: "Particulier",
    desc: "Bons plans, devis et services locaux — 100 % gratuit.",
  },
  {
    to: "/offre-fondateurs", testId: "signup-pro", icon: Store, highlight: true, ribbon: "Offre Fondateur · 0 €",
    label: "Commerçant / Pro",
    desc: "Gagnez en visibilité locale. 0 € pendant 12 mois, sans CB.",
  },
  {
    to: "/associations/inscription", testId: "signup-association", icon: HeartHandshake,
    label: "Association",
    desc: "Animez votre communauté, collectez des dons, publiez vos actus.",
  },
  {
    to: "/clubs/inscription", testId: "signup-club", icon: Trophy,
    label: "Club",
    desc: "Gérez vos membres et faites rayonner votre club localement.",
  },
];

function CatIcon({ name, className }) {
  const Icon = LucideIcons[name] || Tag;
  return <Icon className={className} />;
}

export default function ParticiperVieLocale() {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data || [])).catch(() => {});
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="participer-vie-locale-section">
      <SectionHeader
        eyebrow="100 % local"
        title="Rejoignez la vie locale"
        subtitle="Habitants, commerçants, associations et clubs : créez votre compte gratuit et explorez les univers près de chez vous."
      />

      {/* 1 · Bande d'inscription multi-profils */}
      <div className="mt-6 rounded-3xl bg-gradient-to-br from-[#FFF4EC] to-[#FFE9DD] border border-orange-100 p-5 md:p-7" data-testid="rejoindre-reseau-band">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-1 mb-4">
          <div>
            <h3 className="font-heading text-xl md:text-2xl font-extrabold text-[#172A3A]">Rejoignez le réseau — c'est gratuit</h3>
            <p className="text-sm text-[#66727A] mt-0.5">Particuliers, commerçants, associations et clubs : votre place est ici.</p>
          </div>
          <Link to="/inscription" className="hidden sm:inline-flex items-center gap-1 text-sm font-bold text-[#E05638] hover:gap-1.5 transition-all" data-testid="rejoindre-reseau-all">
            Voir toutes les options <ArrowRight size={15} />
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {SIGNUPS.map((s) => {
            const Icon = s.icon;
            return (
              <Link
                key={s.testId}
                to={s.to}
                data-testid={s.testId}
                className={`group relative flex flex-col rounded-2xl bg-white p-4 hover:-translate-y-1 hover:shadow-md transition-all duration-200 ${
                  s.highlight ? "border-2 border-[#E05638] shadow-sm" : "border border-slate-200 hover:border-[#E05638]/30"
                }`}
              >
                {s.ribbon && (
                  <span className="absolute -top-2.5 left-4 inline-flex items-center rounded-full bg-[#E05638] text-white text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 shadow" data-testid="signup-pro-ribbon">
                    {s.ribbon}
                  </span>
                )}
                <span className={`w-10 h-10 rounded-xl grid place-items-center group-hover:scale-105 transition-transform ${s.highlight ? "bg-[#E05638] text-white" : "bg-[#FFF4EC] text-[#E05638]"}`}>
                  <Icon size={20} />
                </span>
                <div className="mt-2.5 font-heading font-extrabold text-[#172A3A] text-[15px] leading-tight">{s.label}</div>
                <p className="mt-1 text-xs text-[#66727A] flex-1 leading-snug">{s.desc}</p>
                <span className={`mt-3 inline-flex items-center justify-center gap-1.5 h-9 rounded-full text-[13px] font-bold transition-colors ${
                  s.highlight ? "bg-[#E05638] text-white hover:bg-[#c94a2f]" : "bg-[#172A3A] text-white hover:bg-[#2a4258]"
                }`}>
                  Créer mon compte
                </span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* 3 · Univers locaux — barre de pastilles compacte */}
      {cats.length > 0 && (
        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="univers-chips-bar">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#66727A] mr-1">
            <Grid3x3 size={14} className="text-[#E05638]" /> Explorer par univers
          </span>
          {cats.map((c) => (
            <Link
              key={c.id}
              to={`/categories/${c.slug}`}
              data-testid="univers-chip"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[13px] font-semibold text-[#172A3A] hover:border-[#E05638] hover:text-[#E05638] hover:-translate-y-0.5 transition-all duration-150"
            >
              <CatIcon name={c.icon} className="w-3.5 h-3.5 text-[#E05638]" /> {c.name}
            </Link>
          ))}
          <Link
            to="/categories"
            data-testid="univers-chip-all"
            className="inline-flex items-center gap-1 rounded-full bg-[#172A3A] text-white px-3 py-1.5 text-[13px] font-bold hover:bg-[#E05638] transition-colors"
          >
            Toutes <ArrowRight size={13} />
          </Link>
        </div>
      )}
    </section>
  );
}
